/**
 * Cienie na modułach: czy promień od punktu na module w stronę słońca trafia w przeszkodę.
 *
 * Przeszkody to:
 * - trójkąty (dach i ściany budynku, kominy, wiaty, rzędy modułów na dachu płaskim), w drzewie BVH,
 *   bo przy analizie rocznej idzie kilkadziesiąt tysięcy promieni;
 * - kule i walce (korony i pnie drzew dodanych przez użytkownika);
 * - siatka wysokości otoczenia z NMPT (sąsiednie budynki i drzewa), przechodzona krokami po promieniu.
 *
 * Czysty JS bez three.js: działa w testach i może pójść do Web Workera. Układ sceny jak w całym
 * projekcie (x = wschód, y = góra, z = południe), metry.
 */

const EPS = 1e-6

// ── BVH trójkątów ──────────────────────────────────────────────────────────────────────────────

const zbudujBvh = (tri, n) => {
  // Węzeł: [minx, miny, minz, maxx, maxy, maxz, lewy|start, prawy|liczba, lisc]
  const idx = new Int32Array(n)
  for (let i = 0; i < n; i += 1) idx[i] = i
  const cent = new Float32Array(n * 3)
  for (let i = 0; i < n; i += 1) {
    const o = i * 9
    cent[i * 3] = (tri[o] + tri[o + 3] + tri[o + 6]) / 3
    cent[i * 3 + 1] = (tri[o + 1] + tri[o + 4] + tri[o + 7]) / 3
    cent[i * 3 + 2] = (tri[o + 2] + tri[o + 5] + tri[o + 8]) / 3
  }
  const wezly = []
  const buduj = (start, liczba) => {
    let minx = Infinity; let miny = Infinity; let minz = Infinity
    let maxx = -Infinity; let maxy = -Infinity; let maxz = -Infinity
    for (let k = start; k < start + liczba; k += 1) {
      const o = idx[k] * 9
      for (let v = 0; v < 9; v += 3) {
        const x = tri[o + v]; const y = tri[o + v + 1]; const z = tri[o + v + 2]
        if (x < minx) minx = x; if (x > maxx) maxx = x
        if (y < miny) miny = y; if (y > maxy) maxy = y
        if (z < minz) minz = z; if (z > maxz) maxz = z
      }
    }
    const id = wezly.length
    wezly.push(null)
    if (liczba <= 6) {
      wezly[id] = [minx, miny, minz, maxx, maxy, maxz, start, liczba, 1]
      return id
    }
    const dx = maxx - minx; const dy = maxy - miny; const dz = maxz - minz
    const os = dx >= dy && dx >= dz ? 0 : dy >= dz ? 1 : 2
    const fragment = Array.from(idx.subarray(start, start + liczba))
    fragment.sort((a, b) => cent[a * 3 + os] - cent[b * 3 + os])
    for (let k = 0; k < liczba; k += 1) idx[start + k] = fragment[k]
    const pol = liczba >> 1
    const l = buduj(start, pol)
    const p = buduj(start + pol, liczba - pol)
    wezly[id] = [minx, miny, minz, maxx, maxy, maxz, l, p, 0]
    return id
  }
  if (n > 0) buduj(0, n)
  return { wezly, idx }
}

const promienPudelko = (w, ox, oy, oz, ix, iy, iz, tmax) => {
  let t1 = (w[0] - ox) * ix; let t2 = (w[3] - ox) * ix
  let tmin = Math.min(t1, t2); let tm = Math.max(t1, t2)
  t1 = (w[1] - oy) * iy; t2 = (w[4] - oy) * iy
  tmin = Math.max(tmin, Math.min(t1, t2)); tm = Math.min(tm, Math.max(t1, t2))
  t1 = (w[2] - oz) * iz; t2 = (w[5] - oz) * iz
  tmin = Math.max(tmin, Math.min(t1, t2)); tm = Math.min(tm, Math.max(t1, t2))
  return tm >= Math.max(tmin, 0) && tmin < tmax
}

// Möller–Trumbore; true, gdy trafia przed tmax i dalej niż minimalna odległość.
const promienTrojkat = (tri, o, ox, oy, oz, dx, dy, dz, tmin, tmax) => {
  const ax = tri[o]; const ay = tri[o + 1]; const az = tri[o + 2]
  const e1x = tri[o + 3] - ax; const e1y = tri[o + 4] - ay; const e1z = tri[o + 5] - az
  const e2x = tri[o + 6] - ax; const e2y = tri[o + 7] - ay; const e2z = tri[o + 8] - az
  const px = dy * e2z - dz * e2y; const py = dz * e2x - dx * e2z; const pz = dx * e2y - dy * e2x
  const det = e1x * px + e1y * py + e1z * pz
  if (det > -EPS && det < EPS) return false
  const inv = 1 / det
  const tx = ox - ax; const ty = oy - ay; const tz = oz - az
  const u = (tx * px + ty * py + tz * pz) * inv
  if (u < 0 || u > 1) return false
  const qx = ty * e1z - tz * e1y; const qy = tz * e1x - tx * e1z; const qz = tx * e1y - ty * e1x
  const v = (dx * qx + dy * qy + dz * qz) * inv
  if (v < 0 || u + v > 1) return false
  const t = (e2x * qx + e2y * qy + e2z * qz) * inv
  return t > tmin && t < tmax
}

/**
 * Buduje zbiór przeszkód.
 * - `trojkaty`: płaska tablica (9 liczb na trójkąt) albo tablica trójkątów [[ax..cz]];
 * - `znaczniki`: opcjonalnie numer właściciela trójkąta (np. indeks modułu), żeby moduł nie cieniował sam siebie;
 * - `kule`: [{ c: [x,y,z], r }], `walce`: [{ podstawa: [x,y,z], wysokosc, r }];
 * - `siatka`: { dane: Float32Array wysokości w układzie sceny (y), ncols, nrows, x0, z0 (środek komórki [0,0]),
 *   krok (m), maks? } wiersz 0 = najbardziej na północ (najmniejsze z).
 */
export const zbudujPrzeszkody = ({ trojkaty = [], znaczniki = null, kule = [], walce = [], siatka = null } = {}) => {
  const plaskie = trojkaty instanceof Float32Array ? trojkaty : Float32Array.from(trojkaty.flat ? trojkaty.flat() : trojkaty)
  const n = Math.floor(plaskie.length / 9)
  const { wezly, idx } = zbudujBvh(plaskie, n)
  let s = null
  if (siatka) {
    let maks = siatka.maks
    if (maks == null) {
      maks = -Infinity
      for (let i = 0; i < siatka.dane.length; i += 1) if (siatka.dane[i] > maks) maks = siatka.dane[i]
    }
    s = { ...siatka, maks }
  }
  return {
    tri: plaskie, n, wezly, idx, znaczniki: znaczniki ? Int32Array.from(znaczniki) : null,
    kule, walce, siatka: s,
  }
}

const trafiaTrojkaty = (p, ox, oy, oz, dx, dy, dz, pomin) => {
  if (!p.n) return false
  const ix = 1 / (Math.abs(dx) < EPS ? (dx < 0 ? -EPS : EPS) : dx)
  const iy = 1 / (Math.abs(dy) < EPS ? (dy < 0 ? -EPS : EPS) : dy)
  const iz = 1 / (Math.abs(dz) < EPS ? (dz < 0 ? -EPS : EPS) : dz)
  const stos = [0]
  while (stos.length) {
    const w = p.wezly[stos.pop()]
    if (!promienPudelko(w, ox, oy, oz, ix, iy, iz, 1e9)) continue
    if (w[8]) {
      for (let k = w[6]; k < w[6] + w[7]; k += 1) {
        const t = p.idx[k]
        if (pomin >= 0 && p.znaczniki && p.znaczniki[t] === pomin) continue
        if (promienTrojkat(p.tri, t * 9, ox, oy, oz, dx, dy, dz, 1e-3, 1e9)) return true
      }
    } else {
      stos.push(w[6], w[7])
    }
  }
  return false
}

const trafiaKule = (kule, ox, oy, oz, dx, dy, dz) => {
  for (let i = 0; i < kule.length; i += 1) {
    const { c, r } = kule[i]
    const lx = c[0] - ox; const ly = c[1] - oy; const lz = c[2] - oz
    const tca = lx * dx + ly * dy + lz * dz
    if (tca < 0) continue
    const d2 = lx * lx + ly * ly + lz * lz - tca * tca
    if (d2 <= r * r) return true
  }
  return false
}

const trafiaWalce = (walce, ox, oy, oz, dx, dy, dz) => {
  for (let i = 0; i < walce.length; i += 1) {
    const { podstawa: b, wysokosc: h, r } = walce[i]
    // Rzut na płaszczyznę poziomą: odległość osi walca od promienia.
    const px = ox - b[0]; const pz = oz - b[2]
    const a = dx * dx + dz * dz
    if (a < EPS) continue
    const bb = 2 * (px * dx + pz * dz)
    const cc = px * px + pz * pz - r * r
    const delta = bb * bb - 4 * a * cc
    if (delta < 0) continue
    const sq = Math.sqrt(delta)
    const t1 = (-bb - sq) / (2 * a)
    const t2 = (-bb + sq) / (2 * a)
    for (const t of [t1, t2]) {
      if (t <= 1e-3) continue
      const y = oy + dy * t
      if (y >= b[1] && y <= b[1] + h) return true
    }
  }
  return false
}

const wysokoscSiatki = (s, x, z) => {
  const c = (x - s.x0) / s.krok
  const r = (z - s.z0) / s.krok
  if (c < 0 || r < 0 || c > s.ncols - 1 || r > s.nrows - 1) return null
  const c0 = Math.floor(c); const r0 = Math.floor(r)
  const c1 = Math.min(c0 + 1, s.ncols - 1); const r1 = Math.min(r0 + 1, s.nrows - 1)
  const fx = c - c0; const fz = r - r0
  const d = s.dane
  const h00 = d[r0 * s.ncols + c0]; const h10 = d[r0 * s.ncols + c1]
  const h01 = d[r1 * s.ncols + c0]; const h11 = d[r1 * s.ncols + c1]
  if (Number.isNaN(h00 + h10 + h01 + h11)) return null
  return (h00 * (1 - fx) + h10 * fx) * (1 - fz) + (h01 * (1 - fx) + h11 * fx) * fz
}

const trafiaSiatke = (s, ox, oy, oz, dx, dy, dz) => {
  if (!s || (dy <= 0 && oy > s.maks)) return false
  const krok = s.krok * 0.5
  const start = 0.6
  for (let t = start; t < 400; t += krok) {
    const y = oy + dy * t
    if (y > s.maks + 0.01) return false
    const h = wysokoscSiatki(s, ox + dx * t, oz + dz * t)
    if (h === null) {
      // Poza siatką: dalej już nic nie wiemy.
      const x = ox + dx * t; const z = oz + dz * t
      const poza = x < s.x0 - s.krok || z < s.z0 - s.krok
        || x > s.x0 + (s.ncols + 1) * s.krok || z > s.z0 + (s.nrows + 1) * s.krok
      if (poza) return false
      continue
    }
    if (y < h) return true
  }
  return false
}

/** Czy punkt widzi słońce w kierunku `k` (wektor jednostkowy do słońca). `pomin`: znacznik właściciela. */
export const czyZacienione = (p, punkt, k, pomin = -1) => {
  if (k[1] <= 0) return true
  const [ox, oy, oz] = punkt
  const [dx, dy, dz] = k
  return trafiaTrojkaty(p, ox, oy, oz, dx, dy, dz, pomin)
    || trafiaKule(p.kule, ox, oy, oz, dx, dy, dz)
    || trafiaWalce(p.walce, ox, oy, oz, dx, dy, dz)
    || trafiaSiatke(p.siatka, ox, oy, oz, dx, dy, dz)
}

/** Ułamek zacienionych punktów (np. 6 próbek modułu odpowiadających sekcjom diod bocznikujących). */
export const ulamekCienia = (p, punkty, k, pomin = -1) => {
  if (!punkty.length) return 0
  let ile = 0
  for (let i = 0; i < punkty.length; i += 1) if (czyZacienione(p, punkty[i], k, pomin)) ile += 1
  return ile / punkty.length
}

/** Trójkąty prostopadłościanu osiowego (komin, urządzenie): [x0,y0,z0]–[x1,y1,z1], z obrotem wokół y. */
export const trojkatyPudelka = (srodek, wymiary, obrotDeg = 0) => {
  const [cx, cy, cz] = srodek
  const [sx, sy, sz] = wymiary.map((w) => w / 2)
  const a = (obrotDeg * Math.PI) / 180
  const cs = Math.cos(a); const sn = Math.sin(a)
  const w = []
  for (const dx of [-sx, sx]) for (const dy of [-sy, sy]) for (const dz of [-sz, sz]) {
    // Obrót jak rotation.y w three.js.
    w.push([cx + dx * cs + dz * sn, cy + dy, cz - dx * sn + dz * cs])
  }
  const sciany = [[0, 1, 3, 2], [4, 6, 7, 5], [0, 4, 5, 1], [2, 3, 7, 6], [0, 2, 6, 4], [1, 5, 7, 3]]
  const t = []
  for (const [a0, b, c, d] of sciany) {
    t.push(...w[a0], ...w[b], ...w[c], ...w[a0], ...w[c], ...w[d])
  }
  return t
}

/** Trójkąty wielokąta wypukłego (wachlarz). */
export const trojkatyWielokata3d = (wierzcholki) => {
  const t = []
  for (let i = 1; i < wierzcholki.length - 1; i += 1) {
    t.push(...wierzcholki[0], ...wierzcholki[i], ...wierzcholki[i + 1])
  }
  return t
}
